import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { X } from "lucide-react";
import { Spin } from "antd";

import css from "./CloneAvailableDishesToCycleComponent.module.css";
import { availableDishesService } from "../../../services/menu-services/availableDishes.service";
import { cycleAvailableDishesService } from "../../../services/menu-services/cycleAvailableDishes.service";
import { ImageReusable } from "../../../ui";

const CloneAvailableDishesToCycleComponent = ({ activeDay: activeDayProp, onSuccess }) => {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;

    const { cycleConfig } = useSelector((store) => store.appSettings);
    const lengthDays = cycleConfig?.lengthDays || 7;

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm();

    const [isFetching, setIsFetching] = useState(false);
    const [isCloning, setIsCloning] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewDishes, setPreviewDishes] = useState([]);
    const [pendingDate, setPendingDate] = useState(null);
    const [pendingDay, setPendingDay] = useState(null);

    // If activeDay is passed as prop, cycleDay field is not needed
    const submit = async (data) => {
        const targetDay = activeDayProp ?? Number(data?.cycleDay);
        if (!data?.date || !targetDay) return;
        setIsFetching(true);
        try {
            const dishes = await availableDishesService.getAllSAvailableDishes(data.date, true);

            if (!dishes || dishes.length === 0) {
                toast.warning(t("No available dishes found for the selected date"));
                return;
            }

            setPendingDate(data.date);
            setPendingDay(targetDay);
            setPreviewDishes(dishes);
            setPreviewOpen(true);
        } catch (error) {
            console.error("Error fetching available dishes for preview:", error);
            toast.error(t("Error fetching available dishes"));
        } finally {
            setIsFetching(false);
        }
    };

    const handleConfirmClone = async () => {
        setIsCloning(true);
        try {
            const dishPayload = previewDishes
                .map((d) => ({ dishId: d.dishId || d.dish?.id }))
                .filter((d) => d.dishId);

            await cycleAvailableDishesService.editByDay(pendingDay, dishPayload);
            toast.success(t("Available dishes cloned to cycle day successfully"));
            onSuccess?.();
            setPreviewOpen(false);
        } catch (error) {
            console.error("Error cloning available dishes:", error);
            toast.error(t("Error cloning available dishes"));
        } finally {
            setIsCloning(false);
        }
    };

    const closeModal = () => {
        if (!isCloning) setPreviewOpen(false);
    };

    return (
        <>
            <form onSubmit={handleSubmit(submit)} className={css.form}>
                <label className={css.label}>{t("Clone from Date Base Menu")}</label>
                <input
                    type="date"
                    {...register("date", { required: "Date is required" })}
                    className={`${css.input} ${errors.date ? css.inputError : ""}`}
                />
                {errors.date && <p className={css.error}>{errors.date.message}</p>}
                {activeDayProp == null && (
                    <>
                        <select
                            {...register("cycleDay", { required: "Day is required" })}
                            className={`${css.select} ${errors.cycleDay ? css.inputError : ""}`}
                        >
                            <option value="">{t("Select day")}</option>
                            {Array.from({ length: lengthDays }, (_, i) => i + 1).map((day) => (
                                <option key={day} value={day}>
                                    {t("Day")} {day}
                                </option>
                            ))}
                        </select>
                        {errors.cycleDay && <p className={css.error}>{errors.cycleDay.message}</p>}
                    </>
                )}
                <button type="submit" disabled={isSubmitting || isFetching} className={css.button}>
                    {isFetching ? t("Loading...") : "Ok"}
                </button>
            </form>

            {previewOpen && (
                <div className={css.overlay} onClick={closeModal}>
                    <div className={css.modal} onClick={(e) => e.stopPropagation()}>

                        <div className={css.modalHeader}>
                            <h2>
                                {t("Clone available dishes from")} <strong>{pendingDate}</strong> → {t("Day")} <strong>{pendingDay}</strong>
                            </h2>
                            <button className={css.closeButton} onClick={closeModal} disabled={isCloning}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className={css.modalBody}>
                            <p className={css.sectionTitle}>
                                {t("Available dishes to clone")} <span className={css.badge}>{previewDishes.length}</span>
                            </p>
                            <div className={css.grid}>
                                {previewDishes.map((d, idx) => {
                                    const name = d.dish?.name?.[currentLanguage] || d.dish?.name?.en || `Dish ${d.dishId}`;
                                    const img = d.dish?.img;
                                    const weight = d.dish?.weight;
                                    const categories = d.dish?.categories || [];
                                    const categoryName = categories
                                        .map((c) => c.name?.[currentLanguage] || c.name?.en || "")
                                        .filter(Boolean)
                                        .join(", ");

                                    return (
                                        <div key={idx} className={css.card}>
                                            <ImageReusable src={img} alt={name} className={css.cardImage} />
                                            <div className={css.cardBody}>
                                                <div className={css.cardName}>{name}</div>
                                                {categoryName && (
                                                    <span className={css.cardCategory}>{categoryName}</span>
                                                )}
                                                {weight != null && (
                                                    <span className={css.cardMeta}>{weight}g</span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className={css.modalFooter}>
                            <button className={css.cancelButton} onClick={closeModal} disabled={isCloning}>
                                {t("Cancel")}
                            </button>
                            <button className={css.confirmButton} onClick={handleConfirmClone} disabled={isCloning}>
                                {isCloning ? <Spin size="small" /> : t("Confirm Clone")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export { CloneAvailableDishesToCycleComponent };
